// =============================================
// SERVICE: API de Clientes
// =============================================

import { buildApiUrl } from './config';
import { getDefaultHeaders } from './config';
import { getConferenteToken, handleConferenteSessionExpired } from './conferenteAuth';

export interface Cliente {
  id: number;
  empresa_id: number;
  razao_social: string;
  nome_fantasia?: string;
  cnpj: string;
  inscricao_estadual?: string;
  inscricao_municipal?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  contato_nome?: string;
  contato_cargo?: string;
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
  observacoes?: string;
  ativo: boolean;
  total_etiquetas?: number;
  total_areas_atendidas?: number;
  etiqueta_configs?: EtiquetaConfig[];
  etiqueta_modelos?: EtiquetaModelo[];
  criado_em?: string;
  atualizado_em?: string;
}

export interface EtiquetaConfig {
  id?: number;
  cliente_id?: number;
  empresa_id?: number;
  modelo_id?: number;
  nome_campo: string;
  descricao?: string;
  regex_pattern: string;
  posicao_inicio?: number;
  posicao_fim?: number;
  tipo_dado: string;
  obrigatorio: boolean;
  ordem: number;
  ativo: boolean;
  criado_em?: string;
  atualizado_em?: string;
}

export interface EtiquetaModelo {
  id?: number;
  cliente_id?: number;
  empresa_id?: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  campo_numero_pedido?: string | null;
  campo_volume_atual?: string | null;
  campo_total_volumes?: string | null;
  campo_cnpj?: string | null;
  campo_codigo_setor?: string | null;
  total_campos: number;
  configs: EtiquetaConfig[];
  criado_em?: string;
  atualizado_em?: string;
}

export interface ClienteListResponse {
  success: boolean;
  clientes: Cliente[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

const BASE_URL = '/clientes';

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(buildApiUrl(url), {
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...(options.headers || {})
    }
  });

  if (response.status === 401 && getConferenteToken()) {
    handleConferenteSessionExpired();
    return new Promise(() => {});
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Erro ${response.status}`);
  }

  return data;
}

export async function listarClientes(params: {
  page?: number;
  limit?: number;
  busca?: string;
  ativo?: string;
  cidade?: string;
  uf?: string;
} = {}): Promise<ClienteListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.busca) searchParams.set('busca', params.busca);
  if (params.ativo) searchParams.set('ativo', params.ativo);
  if (params.cidade) searchParams.set('cidade', params.cidade);
  if (params.uf) searchParams.set('uf', params.uf);

  const query = searchParams.toString();
  return request<ClienteListResponse>(`${BASE_URL}${query ? `?${query}` : ''}`);
}

export async function buscarCliente(id: number): Promise<ApiResponse<Cliente>> {
  return request<ApiResponse<Cliente>>(`${BASE_URL}/${id}`);
}

export async function criarCliente(data: Partial<Cliente>): Promise<ApiResponse<Cliente>> {
  return request<ApiResponse<Cliente>>(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function atualizarCliente(id: number, data: Partial<Cliente>): Promise<ApiResponse<Cliente>> {
  return request<ApiResponse<Cliente>>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarCliente(id: number): Promise<ApiResponse<null>> {
  return request<ApiResponse<null>>(`${BASE_URL}/${id}`, {
    method: 'DELETE'
  });
}

// Busca por CNPJ
export async function buscarClientePorCnpj(cnpj: string): Promise<{
  success: boolean;
  cliente: Cliente & { etiqueta_configs: EtiquetaConfig[]; etiqueta_modelos: EtiquetaModelo[] };
}> {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return request(`${BASE_URL}/buscar-cnpj/${cnpjLimpo}`);
}

// Configurações de Etiqueta
export async function listarEtiquetaConfigs(clienteId: number): Promise<ApiResponse<EtiquetaConfig[]>> {
  return request<ApiResponse<EtiquetaConfig[]>>(`${BASE_URL}/${clienteId}/etiquetas`);
}

export async function salvarEtiquetaConfigsBatch(clienteId: number, configs: EtiquetaConfig[]): Promise<ApiResponse<EtiquetaConfig[]>> {
  return request<ApiResponse<EtiquetaConfig[]>>(`${BASE_URL}/${clienteId}/etiquetas/batch`, {
    method: 'POST',
    body: JSON.stringify({ configs })
  });
}

export async function testarRegex(regexPattern: string, codigoBarras: string): Promise<ApiResponse<{
  match: boolean;
  resultado: string | null;
  grupos: string[];
  erro?: string;
}>> {
  return request(`${BASE_URL}/testar-regex`, {
    method: 'POST',
    body: JSON.stringify({ regex_pattern: regexPattern, codigo_barras: codigoBarras })
  });
}

// Modelos de Etiqueta
export async function listarModelosEtiqueta(clienteId: number): Promise<ApiResponse<EtiquetaModelo[]>> {
  return request<ApiResponse<EtiquetaModelo[]>>(`${BASE_URL}/${clienteId}/modelos-etiqueta`);
}

export async function criarModeloEtiqueta(clienteId: number, data: {
  nome: string;
  descricao?: string;
  campo_numero_pedido?: string | null;
  campo_volume_atual?: string | null;
  campo_total_volumes?: string | null;
  campo_cnpj?: string | null;
  campo_codigo_setor?: string | null;
  configs: EtiquetaConfig[];
}): Promise<ApiResponse<EtiquetaModelo>> {
  return request<ApiResponse<EtiquetaModelo>>(`${BASE_URL}/${clienteId}/modelos-etiqueta`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function atualizarModeloEtiqueta(clienteId: number, modeloId: number, data: {
  nome: string;
  descricao?: string;
  ativo?: boolean;
  campo_numero_pedido?: string | null;
  campo_volume_atual?: string | null;
  campo_total_volumes?: string | null;
  campo_cnpj?: string | null;
  campo_codigo_setor?: string | null;
  configs: EtiquetaConfig[];
}): Promise<ApiResponse<EtiquetaModelo>> {
  return request<ApiResponse<EtiquetaModelo>>(`${BASE_URL}/${clienteId}/modelos-etiqueta/${modeloId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarModeloEtiqueta(clienteId: number, modeloId: number): Promise<ApiResponse<null>> {
  return request<ApiResponse<null>>(`${BASE_URL}/modelos-etiqueta/${modeloId}`, {
    method: 'DELETE'
  });
}

// Áreas Atendidas pelo Cliente
export interface AreaAtendida {
  id: number;
  area_entrega_id: number;
  nome: string;
  codigo?: string;
  regiao?: string;
  filial_id?: number;
  criado_em?: string;
}

export async function listarAreasAtendidas(clienteId: number): Promise<{ success: boolean; areas: AreaAtendida[] }> {
  return request(`${BASE_URL}/${clienteId}/areas-atendidas`);
}

export async function salvarAreasAtendidas(clienteId: number, areaIds: number[]): Promise<{ success: boolean; areas: AreaAtendida[]; message?: string }> {
  return request(`${BASE_URL}/${clienteId}/areas-atendidas`, {
    method: 'PUT',
    body: JSON.stringify({ area_ids: areaIds })
  });
}
